import React from "react"
import { useState, useEffect, useCallback } from "react"
import styled from "styled-components"
import { useAuth } from "../../context/AuthProvider.tsx"
import { useParams, Link, useNavigate } from "react-router-dom"
import { fetchAuthSession } from "@aws-amplify/auth"
import {
  Typography,
  MenuItem,
  Box,
  IconButton,
  Button,
  ThemeProvider,
  createTheme,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItemText,
  Snackbar,
  Alert,
  Avatar
} from "@mui/material"
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  Lock as LockIcon
} from "@mui/icons-material"
import UpdateProfilePicture from "../../api/UpdateProfilePicture.ts"
import AddFriend from "../../api/AddFriend.ts"
import { motion, AnimatePresence } from "framer-motion"
import { useInView } from "react-intersection-observer"
import UpgradeModal from "../../components/UpgradeModal.tsx"
import LoadingCircle from '../../components/LoadingCircle.tsx'
import { ConfirmationDialog, EditBioDialog, SelectCapsuleDialog } from '../../components/Dialogs.tsx'

interface FeaturedCapsule {
  capsuleID: number
  title: string
  description: string
  images: []
  imageUrl: string
  unlock: string
}

const DEFAULT_PROFILE_PIC = "https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/profilepic.svg"

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#0A0F15",
      paper: "#121A21",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
    primary: {
      main: "#7B2CBF",
    },
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#0A0F15",
          color: "#FFFFFF",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: "#94ADC7",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#94ADC7",
            },
            "&:hover fieldset": {
              borderColor: "#FFFFFF",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#7B2CBF",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#94ADC7",
          },
          "& .MuiInputBase-input": {
            color: "#FFFFFF",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
        },
        contained: {
          backgroundColor: "#7B2CBF",
          "&:hover": {
            backgroundColor: "#6A24A6",
          },
        },
      },
    },
  },
})

export const UserInfo: React.FC = () => {
  const { user, logout } = useAuth()
  const { id } = useParams<{ id: string }>()
  const [profileData, setProfileData] = useState<any>(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [newBio, setNewBio] = useState("")
  const [friend, setFriend] = useState<string>("")
  const [featured, setFeatured] = useState<FeaturedCapsule[]>([])
  const [openSelectCapsuleDialog, setOpenSelectCapsuleDialog] = useState(false)
  const [availableCapsules, setAvailableCapsules] = useState<FeaturedCapsule[]>([])
  const [selectedCapsule, setSelectedCapsule] = useState<string>("")
  const [openRemoveFriendDialog, setOpenRemoveFriendDialog] = useState(false)
  const [profilePicKey, setProfilePicKey] = useState(Date.now())
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false)
  const [loadingCapsules, setLoadingCapsules] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false) // Loading state for profile picture upload
  const [uploadError, setUploadError] = useState<string | null>(null) // Error state for profile picture upload
  const [uploadSuccess, setUploadSuccess] = useState(false) // Success state for profile picture upload
  const navigate = useNavigate()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const myProfile = id === user?.username

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const fetchUserData = useCallback(async () => {
    if (!id) return

    try {
      const { tokens } = await fetchAuthSession()
      const authToken = tokens?.accessToken?.toString()
      const response = await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/user/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": authToken!,
        },
      })
      const data = await response.json()
      setProfileData(data)
      setNewBio(data.bio || "")
    } catch (error) {
      console.error("Error fetching user profile:", error)
    }
  }, [id])

  const fetchFriendData = useCallback(async () => {
    if (!user?.username || !id) return

    try {
      const { tokens } = await fetchAuthSession()
      const authToken = tokens?.accessToken?.toString()
      const payload = {
        userID: user.username,
      }
      const response = await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/friend/status/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": authToken!,
        },
        body: JSON.stringify(payload),
      })
      const data = await response.json()
      setFriend(data)
    } catch (error) {
      console.error("Error fetching friend status:", error)
    }
  }, [user, id])

  useEffect(() => {
    fetchUserData()
    fetchFriendData()
  }, [fetchUserData, fetchFriendData])

  const handleProfilePicClick = async () => {
    if (!myProfile) return

    const fileInput = document.createElement("input")
    fileInput.type = "file"
    fileInput.accept = "image/*"

    fileInput.onchange = async (event: any) => {
      const file = event.target.files[0]
      if (file) {
        setUploadingProfilePic(true) // Start loading
        setUploadError(null) // Reset error state
        setUploadSuccess(false) // Reset success state

        try {
          const compressedBase64 = await compressImage(file)
          await UpdateProfilePicture(user!.username, compressedBase64)
          setProfilePicKey(Date.now())
          setProfileData((prevData) => ({
            ...prevData,
            picture: `${prevData.picture}?t=${Date.now()}`,
          }))
          setUploadSuccess(true) // Show success feedback
        } catch (error) {
          console.error("Error uploading profile picture:", error)
          setUploadError("Failed to upload profile picture. Please try again.") // Show error feedback
        } finally {
          setUploadingProfilePic(false) // Stop loading
        }

        // Reset the file input to allow the same file to be selected again
        fileInput.value = ""
      }
    }

    setTimeout(() => {
      fileInput.click()
    }, 100)
  }

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (event) => {
        const img = new Image()
        img.src = event.target!.result as string
        img.onload = () => {
          const canvas = document.createElement("canvas")
          const ctx = canvas.getContext("2d")!
          const MAX_WIDTH = 800
          const MAX_HEIGHT = 800
          let width = img.width
          let height = img.height

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width
              width = MAX_WIDTH
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height
              height = MAX_HEIGHT
            }
          }

          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0, width, height)

          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7)
          resolve(compressedBase64.split(",")[1])
        }
      }
      reader.onerror = (error) => reject(error)
    })
  }

  const handleOpenDialog = () => {
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  const handleOpenUpgradeModal = () => {
    setUpgradeModalOpen(true)
  }

  const handleCloseUpgradeModal = () => {
    setUpgradeModalOpen(false)
  }

  const handleUpdateBio = async () => {
    try {
      const { tokens } = await fetchAuthSession()
      const authToken = tokens?.accessToken?.toString()
      const payload = {
        username: user?.username,
        bio: newBio,
      }
      await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/user/bio/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": authToken!,
        },
        body: JSON.stringify(payload),
      })
      setProfileData({ ...profileData, bio: newBio })
      handleCloseDialog()
    } catch (error) {
      console.error("Error updating bio:", error)
    }
  }

  const handleRemoveFriend = async () => {
    if (!user || !id) return

    try {
      setOpenRemoveFriendDialog(false)
      const { tokens } = await fetchAuthSession()
      const authToken = tokens?.accessToken?.toString()
      const payload = {
        userID: user.username,
      }
      await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/friend/remove/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": authToken!,
        },
        body: JSON.stringify(payload),
      })
      setFriend("false")
    } catch (error) {
      console.error("Error removing friend:", error)
    }
  }

  const handleOpenRemoveFriendDialog = () => {
    if (friend === "accepted") {
      setOpenRemoveFriendDialog(true)
    }
  }

  const handleCloseRemoveFriendDialog = () => {
    setOpenRemoveFriendDialog(false)
  }

  const handleAddFriend = async () => {
    if (!user || !id) return

    try {
      setFriend("pending")
      await AddFriend(user.username, id)
    } catch (error) {
      console.error("Error adding friend:", error)
      setFriend("false")
    }
  }

  const getFriendButtonContent = () => {
    switch (friend) {
      case "accepted":
        return {
          text: "Friends",
          onClick: handleOpenRemoveFriendDialog,
          disabled: false,
        }
      case "pending":
      case "incoming":
        return {
          text: "Pending",
          onClick: undefined,
          disabled: true,
        }
      case "false":
      default:
        return {
          text: "Add Friend",
          onClick: handleAddFriend,
          disabled: false,
        }
    }
  }

  const fetchFeaturedCapsules = useCallback(async () => {
    if (!id) return

    try {
      setLoadingCapsules(true)
      const { tokens } = await fetchAuthSession()
      const authToken = tokens?.accessToken?.toString()
      const response = await fetch(
        `https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/capsules/featured/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": authToken!,
          },
        },
      )
      const data = await response.json()
      const capsules = data.featuredCapsules || []

      setFeatured(capsules)
      setLoadingCapsules(false)
    } catch (error) {
      console.error("Error fetching featured capsules:", error)
    }
  }, [id])

  useEffect(() => {
    if (inView) {
      fetchFeaturedCapsules()
    }
  }, [inView, fetchFeaturedCapsules])

  const handleRemoveFeaturedCapsule = async (capsuleID: number) => {
    try {
      const { tokens } = await fetchAuthSession()
      const authToken = tokens?.accessToken?.toString()
      const payload = {
        capsuleID: capsuleID,
      }
      await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/capsules/featured/${id}?action=remove`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": authToken!,
        },
        body: JSON.stringify(payload),
      })
      setFeatured(featured.filter((capsule) => capsule.capsuleID !== capsuleID))
    } catch (error) {
      console.error("Error removing featured capsule:", error)
    }
  }

  const handleOpenSelectCapsuleDialog = async () => {
    try {
      setOpenSelectCapsuleDialog(true)
      const { tokens } = await fetchAuthSession()
      const authToken = tokens?.accessToken?.toString()
      const response = await fetch(
        `https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/capsules/${id}?owned=true`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": authToken!,
          },
        },
      )
      const data = await response.json()

      // Filter out capsules that are already in displayedCapsules and only include unlocked capsules
      const filteredData = data.filter(
        (capsule) => 
          !displayedCapsules.some((displayedCapsule) => displayedCapsule.capsuleID === capsule.capsuleID) &&
          new Date() > new Date(capsule.unlock)
      )

      setAvailableCapsules(filteredData)
    } catch (error) {
      console.error("Error fetching available capsules:", error)
    }
  }

  const handleCloseSelectCapsuleDialog = () => {
    setOpenSelectCapsuleDialog(false)
  }

  const countWords = (text: string) => {
    return text.trim().split(/\s+/).length
  }

  const handleSelectCapsule = async () => {
    if (!selectedCapsule) return

    try {
      setOpenSelectCapsuleDialog(false)
      const { tokens } = await fetchAuthSession()
      const authToken = tokens?.accessToken?.toString()
      const capsule = availableCapsules.find((c) => c.capsuleID.toString() === selectedCapsule)
      if (!capsule) return

      const payload = {
        capsuleID: capsule.capsuleID,
      }

      await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/capsules/featured/${id}?action=add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": authToken!,
        },
        body: JSON.stringify(payload),
      })
      setFeatured([...featured, capsule])
      setSelectedCapsule("")
      window.location.reload()
    } catch (error) {
      console.error("Error adding featured capsule:", error)
    }
  }

  const handleManageBilling = () => {
    // Implement billing management logic here
    if (user?.subscriptionStatus !== "premium") {
      handleOpenUpgradeModal()
    } else {
      // Get the user's email address from their account
      const userEmail = user?.email;
      
      // Base URL for the Stripe billing portal
      const billingPortalUrl = "https://billing.stripe.com/p/login/28obM35W5aIW7E49AA";
      
      // If we have the user's email, append it as a query parameter for auto-login
      if (userEmail) {
        const encodedEmail = encodeURIComponent(userEmail);
        window.location.href = `${billingPortalUrl}?prefilled_email=${encodedEmail}`;
      } else {
        // Fallback to the regular URL if email isn't available
        window.location.href = billingPortalUrl;
      }
    }
  }

  const displayedCapsules = featured.slice(0, 4)
  const remainingSlots = myProfile ? 4 - displayedCapsules.length : 0

  if (!profileData)
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          bgcolor: '#0A0F15'
        }}
      >
        <LoadingCircle />
      </Box>
    );

  if (profileData.message === "Failed to retrieve user")
    return (
      <center>
        <p>User not found</p>
      </center>
    )

    const CountdownOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    font-size: 18px;
    font-weight: bold;
    border-radius: 12px;
    `

  const CapsuleCard = styled.article`
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
  `

  const CapsuleCardContent = ({ capsule, myProfile, handleRemoveFeaturedCapsule }) => {
    const [timeLeft, setTimeLeft] = useState("")

    useEffect(() => {
      const updateCountdown = () => {
        const now = new Date()
        const unlockDate = new Date('01/01/2026')
        const difference = unlockDate.getTime() - now.getTime()

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24))
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
          setTimeLeft(`${days}d ${hours}h ${minutes}m`)
        } else {
          setTimeLeft("Unlocked")
        }
      }

      updateCountdown()
      const timer = setInterval(updateCountdown, 60000)

      return () => clearInterval(timer)
    }, [capsule.unlock])

    const isLocked = capsule.title === '2026'

    return (
      <CapsuleCard>
        <CapsuleLink to={isLocked ? '' : `/capsule/${capsule.capsuleID}`}>
        <div style={{ position: "relative" }}>
          <CapsuleImage src={capsule.imageUrl || "/placeholder.svg"} alt={capsule.title} isLocked={isLocked} />
          {isLocked && (
            <CountdownOverlay>
              <LockIcon sx={{ fontSize: 40, marginBottom: 1 }} />
              <Typography variant="h6" sx={{ marginBottom: 1 }}>Locked</Typography>
              <Typography variant="body2">{timeLeft}</Typography>
            </CountdownOverlay>
          )}
        </div>
          <CapsuleTitle>{capsule.title}</CapsuleTitle>
        </CapsuleLink>
        {myProfile && (
          <RemoveButton onClick={() => handleRemoveFeaturedCapsule(capsule.capsuleID)}>
            <CloseIcon />
          </RemoveButton>
        )}
      </CapsuleCard>
    )
  }

  const CapsuleImage = styled.img<{ isLocked: boolean }>`
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 12px;
    transition: transform 0.2s;
    margin-bottom: 10px;
    filter: ${(props) => (props.isLocked ? "blur(5px)" : "none")};

    &:hover {
      transform: scale(1.02);
    }
  `

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <UserInfoSection>
        <UserProfile>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <ProfileImageWrapper onClick={handleProfilePicClick}>
              <ProfileImage
                key={profilePicKey}
                src={`${profileData.picture || DEFAULT_PROFILE_PIC}?t=${profilePicKey}`}
                alt="Profile Picture"
              />
              {myProfile && (
                <UpdateOverlay>
                  <UpdateText>Update</UpdateText>
                </UpdateOverlay>
              )}
              {uploadingProfilePic && (
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2
                  }}
                >
                  <LoadingCircle />
                </Box>
              )}
            </ProfileImageWrapper>
          </motion.div>
          <UserDetails hasBio={!!profileData.bio}>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Box display="flex" flexDirection="column" width="100%">
                <NameContainer>
                  <UserName>{profileData.name}</UserName>
                  {myProfile && !isMobile && (
                    <MenuButton onClick={() => setMenuOpen(true)}>
                      <MenuIcon />
                    </MenuButton>
                  )}
                </NameContainer>
                <UserUsername>@{profileData.username}</UserUsername>
              </Box>
              {profileData.bio && <UserBio>{profileData.bio}</UserBio>}
              {myProfile ? (
                <EditBioButton onClick={handleOpenDialog}>
                  <EditIcon sx={{ marginRight: 1 }} />
                  Edit Bio
                </EditBioButton>
              ) : (
                <AddFriendButton
                  aria-label={getFriendButtonContent().text}
                  onClick={getFriendButtonContent().onClick}
                  disabled={getFriendButtonContent().disabled}
                >
                  {getFriendButtonContent().text}
                </AddFriendButton>
              )}
            </motion.div>
          </UserDetails>
        </UserProfile>
      </UserInfoSection>
      {/* Snackbar for error feedback */}
      <Snackbar
        open={!!uploadError}
        autoHideDuration={6000}
        onClose={() => setUploadError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      </Snackbar>

      {/* Snackbar for success feedback */}
      <Snackbar
        open={uploadSuccess}
        autoHideDuration={3000}
        onClose={() => setUploadSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setUploadSuccess(false)}>
          Profile picture updated successfully!
        </Alert>
      </Snackbar>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <FriendsContainer>
          <FriendsIcon src="https://cdn.builder.io/api/v1/image/assets/TEMP/ecb63f328e402a7a2e3cde7a269e271561e2b968b57abad25f94bd54184c92b4?placeholderIfAbsent=true&apiKey=d4fea287e2934d0a83cc04f2895e928a" alt="" />
          <FriendsInfo>
            <FriendsTitle>Friends</FriendsTitle>
            <FriendsActions>
              <FriendsCount>{profileData.friendCount} friends</FriendsCount>
            </FriendsActions>
          </FriendsInfo>
        </FriendsContainer>
        {myProfile && user?.subscriptionStatus !== 'premium' && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <UpgradeContainer onClick={handleOpenUpgradeModal}>
              <UpgradeContent>
                <UpgradeIcon>
                  <StarIcon sx={{ fontSize: 24, color: '#7B2CBF' }} />
                </UpgradeIcon>
                <UpgradeInfo>
                  <UpgradeTitle>Upgrade to Premium</UpgradeTitle>
                  <UpgradeDescription>Unlock unlimited capsules and more!</UpgradeDescription>
                </UpgradeInfo>
              </UpgradeContent>
              <UpgradeButton>
                <ArrowForwardIcon />
              </UpgradeButton>
            </UpgradeContainer>
          </motion.div>
        )}
      </motion.div>
      <Section ref={ref}>
        <SectionTitle>Featured Capsules</SectionTitle>
        {loadingCapsules ? (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '200px',
              width: '100%'
            }}
          >
            <LoadingCircle />
          </Box>
        ) : (
          <>
            <CapsulesGrid>
              <AnimatePresence>
                {featured.map((capsule, index) => (
                  <motion.div
                    key={capsule.capsuleID}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <CapsuleCardContent
                      capsule={capsule}
                      myProfile={myProfile}
                      handleRemoveFeaturedCapsule={handleRemoveFeaturedCapsule}
                    />
                  </motion.div>
                ))}
                {myProfile && [...Array(remainingSlots)].map((_, index) => (
                  <motion.div
                    key={`add-${index}`}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ duration: 0.3, delay: (displayedCapsules.length + index) * 0.1 }}
                  >
                    <AddCapsuleCard onClick={handleOpenSelectCapsuleDialog}>
                      <AddIconWrapper>
                        <AddIcon style={{ fontSize: 48 }} />
                      </AddIconWrapper>
                      <CapsuleTitle>Add New</CapsuleTitle>
                    </AddCapsuleCard>
                  </motion.div>
                ))}
              </AnimatePresence>
            </CapsulesGrid>
            {featured.length === 0 && !myProfile && (
              <NoCapsulesMessage>
                This user hasn't featured any capsules yet.
              </NoCapsulesMessage>
            )}
          </>
        )}
      </Section>
      <UpgradeModal
        open={upgradeModalOpen}
        onClose={handleCloseUpgradeModal}
      />
      <ThemeProvider theme={darkTheme}>
        <EditBioDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onConfirm={handleUpdateBio}
          bio={newBio}
          setBio={setNewBio}
          wordCount={countWords(newBio)}
        />

        <ConfirmationDialog
          open={openRemoveFriendDialog}
          onClose={handleCloseRemoveFriendDialog}
          onConfirm={handleRemoveFriend}
          title="Remove Friend"
          message={`Are you sure you want to remove ${profileData.name} from your friends?`}
          confirmText="Remove"
          confirmColor="error"
        />

        <SelectCapsuleDialog
          open={openSelectCapsuleDialog}
          onClose={handleCloseSelectCapsuleDialog}
          onConfirm={handleSelectCapsule}
          availableCapsules={availableCapsules}
          selectedCapsule={selectedCapsule}
          setSelectedCapsule={setSelectedCapsule}
        />
      </ThemeProvider>
      {isMobile && myProfile &&
        <MenuButton onClick={() => setMenuOpen(true)}>
          <MenuIcon />
        </MenuButton>
      }
      <StyledDrawer
        anchor="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      >
        <MenuList>
          <MenuItem onClick={() => navigate("/about")}>
            <ListItemText sx={{ color: 'white' }} primary="About Us" />
          </MenuItem>
          <MenuItem onClick={() => navigate("/privacy")}>
            <ListItemText sx={{ color: 'white' }} primary="Privacy Policy" />
          </MenuItem>
          <MenuItem onClick={() => navigate("/terms")}>
            <ListItemText sx={{ color: 'white' }} primary="Terms and Conditions" />
          </MenuItem>
          <MenuItem onClick={() => navigate("/contact")}>
            <ListItemText sx={{ color: 'white' }} primary="Contact Us"/>
          </MenuItem>
          <MenuItem onClick={handleManageBilling}>
            <ListItemText sx={{ color: 'white' }} primary="Manage Billing" />
          </MenuItem>
        </MenuList>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', position: 'absolute', bottom: isMobile ? 60 : 0, borderTop: '1px solid #e1e8ed', py: 2, pl: '10px' }}>
            <Avatar sx={{ width: 50, height: 50, mr: 2, bgcolor: 'white', color: 'black' }} src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${user!.username}/profilepic.jpeg`}>{user!.username.charAt(0).toUpperCase()}</Avatar>
            <Typography variant="body1" noWrap sx={{ flexGrow: 1, fontWeight: 'bold', color: "white" }}>@{user!.username}</Typography>
            <IconButton onClick={logout} size="large" sx={{ color: '#e74c3c' }}>
              <LogoutIcon />
            </IconButton>
          </Box>
      </StyledDrawer>
    </motion.div>
  );
}

const NameContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  @media (max-width: 767px) {
    justify-content: center;
  }
`

const UpgradeContainer = styled.div`
  display: flex;
  margin-left: 3%;
  margin-right: 3%;
  margin-bottom: 3%;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  position: relative;
  height: 80px;
  border: 4px solid transparent;
  background-clip: padding-box;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    background: linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff);
    z-index: -1;
    border-radius: 16px;
  }

  &:hover {
    box-shadow: 0 10px 20px rgba(123, 44, 191, 0.2);
  }

  @media (min-width: 768px) {
    padding: 20px 32px;
  }
`

const UpgradeContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const UpgradeIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(123, 44, 191, 0.1);
  border-radius: 50%;
`

const UpgradeInfo = styled.div`
  flex: 1;
  margin-top: 15px;
`

const UpgradeTitle = styled.h3`
  color: #7B2CBF;
  font: 700 16px/1.2 Inter, sans-serif;
  margin-bottom: 4px;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`

const UpgradeDescription = styled.p`
  color: #666;
  font: 400 12px Inter, sans-serif;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`

const UpgradeButton = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(123, 44, 191, 0.1);
  border-radius: 50%;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(123, 44, 191, 0.2);
  }

  svg {
    color: #7B2CBF;
    font-size: 20px;
  }
`

const UserInfoSection = styled.section`
  background: #0A0F15;
  padding: 24px;
  border-radius: 12px;

  @media (min-width: 768px) {
    padding: 32px;
  }
`

const UserProfile = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: center;
  }
`

const ProfileImageWrapper = styled.div`
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  border-radius: 50%;
  width: 128px;
  height: 128px;

  @media (min-width: 768px) {
    width: 160px;
    height: 160px;
  }
`

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const UpdateOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`

const UpdateText = styled.span`
  color: white;
  font-size: 16px;
  font-weight: bold;
`

interface UserDetailsProps {
  hasBio: boolean
}

const UserDetails = styled.div<UserDetailsProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  flex-grow: 1;

  @media (max-width: 767px) {
    width: 100%;
    align-items: center;
    text-align: center;
  }

  @media (min-width: 768px) {
    padding-left: 10px;
  }
`

const UserName = styled.h2`
  align: center;
  color: #fff;
  font: 700 22px/1.2 Inter, sans-serif;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 28px;
  }
`

const UserUsername = styled.p`
  color: #94ADC7;
  font: 400 16px/1.5 Inter, sans-serif;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`

const UserBio = styled.p`
  color: #94ADC7;
  font: 400 16px/1.5 Inter, sans-serif;
  margin: 24px 0 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`

const AddFriendButton = styled.button<{ disabled?: boolean }>`
  background: #243647;
  color: #fff;
  font: 700 14px Inter, sans-serif;
  padding: 12px 24px;
  border-radius: 20px;
  border: none;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  transition: background-color 0.2s;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  width: 100%;
  margin-top: 12px;
  text-transform: none;

  &:hover {
    background: ${(props) => (props.disabled ? "#243647" : "#2c4259")};
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  @media (min-width: 768px) {
    font-size: 16px;
    padding: 14px 28px;
  }
`

const EditBioButton = styled(Button)`
  && {
    color: #fff;
    background-color: #243647;
    text-transform: none;
    font: 700 14px Inter, sans-serif;
    padding: 12px 24px;
    border-radius: 20px;
    transition: background-color 0.2s;
    width: 100%;
    margin-top: 12px;

    &:hover {
      background-color: #2c4259;
    }

    @media (min-width: 768px) {
      font-size: 16px;
      padding: 14px 28px;
    }
  }
`

const FriendsContainer = styled.section`
  display: flex;
  gap: 16px;
  padding: 24px;
  background: #0A0F15;
  border-radius: 12px;

  @media (min-width: 768px) {
    padding: 32px;
    gap: 24px;
  }
`

const FriendsIcon = styled.img`
  width: 48px;
  height: 48px;
  padding: 12px;
  background: #243647;
  border-radius: 8px;

  @media (min-width: 768px) {
    width: 56px;
    height: 56px;
  }
`

const FriendsInfo = styled.div`
  flex: 1;
`

const FriendsTitle = styled.h3`
  color: #fff;
  font: 500 16px/1.2 Inter, sans-serif;
  margin-bottom: 8px;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`

const FriendsActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`

const FriendsCount = styled.p`
  color: #94ADC7;
  font: 400 14px Inter, sans-serif;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`

const Section = styled.section`
  background: #0A0F15;
  padding: 24px;
  border-radius: 12px;

  @media (min-width: 768px) {
    padding: 32px;
  }
`

const SectionTitle = styled.h2`
  color: #fff;
  font: 700 22px/1 Inter, sans-serif;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    font-size: 28px;
    margin-bottom: 32px;
  }
`

const CapsulesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
`

const CapsuleCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
`

const CapsuleImage = styled.img<{ isLocked: boolean }>`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 12px;
  transition: transform 0.2s;
  margin-bottom: 10px;
  filter: ${(props) => (props.isLocked ? "blur(5px)" : "none")};

  &:hover {
    transform: scale(1.02);
  }
`

const CapsuleTitle = styled.h3`
  color: #fff;
  font: 500 16px Inter, sans-serif;
  text-align: center;
  margin: 0;
  padding: 8px 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`

const AddCapsuleCard = styled(CapsuleCard)`
  cursor: pointer;
`

const AddIconWrapper = styled.div`
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  transition: background-color 0.2s, transform 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.02);
  }
`

const CapsuleLink = styled(Link)`
  text-decoration: none;
`

const NoCapsulesMessage = styled.p`
  color: #94ADC7;
  font: 400 16px/1.5 Inter, sans-serif;
  text-align: left;
  margin-top: 24px;
`

const RemoveButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`

const MenuButton = styled(IconButton)`
  color: white;

  @media (max-width: 768px) {
    position: absolute;
    top: 16px;
    right: 16px;
  }
`
const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: 250px;
    background-color: #0A0F15;
  }
`

const MenuList = styled(List)`
  padding-top: 20px;
`

export default UserInfo

